/**
 * Copyright (c) Siemens 2016 - 2025
 * SPDX-License-Identifier: MIT
 */
import { ChangeDetectorRef, Component, inject, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SiIconModule } from '@siemens/element-ng/icon';
import { ModalRef } from '@siemens/element-ng/modal';
import { ChatService } from 'src/app/services/chat.service';
import { IMessage } from 'src/app/interfaces/streaming-messages.interface';
import {
  FeedbackCategory,
  FeedbackCategoriesResponse
} from 'src/app/interfaces/feedback.interface';

@Component({
  selector: 'app-feedback-modal',
  imports: [CommonModule, FormsModule, TranslateModule, SiIconModule],
  templateUrl: './feedback-modal.component.html',
  host: { class: 'feedback-modal' }
})
export class FeedbackModalComponent implements OnInit {
  modalRef = inject(ModalRef);
  chatService = inject(ChatService);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() feedbackType: 'up' | 'down' = 'up';
  @Input() command: IMessage | null = null;
  feedbackMessage: string = '';
  selectedCategory: string = '';
  showValidationError: boolean = false;
  loadingCategories: boolean = true;
  feedbackCategories: FeedbackCategory[] = [];

  ngOnInit() {
    // feedbackType and command are set via @Input() by SiModalService inputValues
    this.loadFeedbackCategories();
  }

  loadFeedbackCategories() {
    this.loadingCategories = true;
    this.chatService.getFeedbackCategories().subscribe({
      next: (response: FeedbackCategoriesResponse) => {
        if (response.categories && Array.isArray(response.categories)) {
          // Map backend categories to frontend format
          this.feedbackCategories = response.categories.map((cat) => ({
            value: cat.value,
            label: cat.label,
            requiresMessage: cat.value === 'others' // Only "others" requires message
          }));
        }
        this.loadingCategories = false;
        this.cdr.detectChanges();
      },
      error: () => {
        // Error loading feedback categories: log and leave categories empty
        this.loadingCategories = false;
        this.cdr.detectChanges();
        this.feedbackCategories = [];
      }
    });
  }

  onCategoryChange() {
    // Reset feedback message when category changes
    this.feedbackMessage = '';
  }

  onMessageChange() {
    // Hide validation error when user starts typing
    if (this.showValidationError) {
      this.showValidationError = false;
    }
  }

  get shouldShowTextBox(): boolean {
    return this.selectedCategory !== '';
  }

  get isTextBoxRequired(): boolean {
    if (this.feedbackType === 'up') {
      return false; // Optional for thumbs up
    }
    // For thumbs down, required only for "Others" category
    const category = this.feedbackCategories.find(cat => cat.value === this.selectedCategory);
    return category?.requiresMessage || false;
  }

  get isSubmitDisabled(): boolean {
    // For thumbs down, category must be selected
    if (!this.selectedCategory) {
      return true;
    }
    // If "Others" is selected, message is required
    if (this.isTextBoxRequired && !this.feedbackMessage.trim()) {
      return true;
    }
    return false;
  }

  closeModal() {
    this.skipFeedback();
  }

  skipFeedback() {
    // Save the rating (up/down) without feedback message
    const feedbackData = {
      type: this.feedbackType,
      message: '', // Empty message when skipping
      category: '',
      command: this.command
    };
    this.modalRef.hide(feedbackData);
  }

  submitFeedback() {
    this.showValidationError = false;
    const userMessage = this.feedbackMessage.trim();
    // Return the feedback data to the parent component
    const feedbackData = {
      type: this.feedbackType,
      message: userMessage,
      category: this.selectedCategory,
      command: this.command
    };
    this.modalRef.hide(feedbackData);
  }
}
